import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Settings2, Star, Globe, MapPin, Phone, Ban, UserPlus, Bookmark, Trash2, ShieldAlert, Loader2 } from "lucide-react";

const errMsg = (e) => e?.response?.data?.detail?.toString?.() || "Something went wrong";
const STATUSES = ["all", "new", "saved", "converted", "rejected"];

function SourceSettings({ open, onClose, providers, onSaved }) {
  const [keys, setKeys] = useState({ google_api_key: "", youtube_api_key: "" });
  const masked = (id) => providers.find((p) => p.id === id)?.key_masked;
  const remove = async (f) => {
    try { await api.put("/lead-finder/settings", { [f]: "" }); toast.success("Key removed"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    const body = Object.fromEntries(Object.entries(keys).filter(([, v]) => v.trim()));
    if (!Object.keys(body).length) return toast.error("Enter at least one key");
    try { await api.put("/lead-finder/settings", body); toast.success("Source keys saved"); setKeys({ google_api_key: "", youtube_api_key: "" }); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white" data-testid="source-settings-dialog">
        <DialogHeader><DialogTitle>Source settings</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Keys are stored encrypted for your company only and never shown in full again.</p>
        {[["google_api_key", "google", "Google Places API (New) key"], ["youtube_api_key", "youtube", "YouTube Data API v3 key"]].map(([f, id, label]) => (
          <div key={f} className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">{label}</label>
              {masked(id) && <button type="button" onClick={() => remove(f)} className="text-xs text-rose-600 hover:underline" data-testid={`remove-key-${id}`}>Remove</button>}
            </div>
            <Input type="password" placeholder={masked(id) ? `Saved: ${masked(id)} — paste to replace` : "Paste key"}
              value={keys[f]} onChange={(e) => setKeys({ ...keys, [f]: e.target.value })} data-testid={`key-input-${id}`} />
          </div>
        ))}
        <DialogFooter><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="save-source-keys">Save keys</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SearchBar({ providers, onResults }) {
  const [f, setF] = useState({ provider: "demo", query: "", location: "", limit: 20 });
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (f.query.trim().length < 2) return toast.error("Type what kind of business to find");
    setBusy(true);
    try { const { data } = await api.post("/lead-finder/search", { ...f, location: f.location || null, limit: Number(f.limit) }); toast.success(`${data.results.length} businesses found`); onResults(data.search_id); }
    catch (e2) { toast.error(errMsg(e2)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={run} className="grid grid-cols-1 md:grid-cols-[260px_1fr_1fr_90px_auto] gap-2" data-testid="finder-search-form">
      <Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}>
        <SelectTrigger data-testid="finder-provider-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          {providers.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`provider-option-${p.id}`}>{p.label}{!p.configured ? " (key needed)" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input placeholder="e.g. real estate agents, clinics, restaurants" value={f.query} onChange={(e) => setF({ ...f, query: e.target.value })} data-testid="finder-query-input" />
      <Input placeholder="City or area (optional)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="finder-location-input" />
      <Input type="number" min={1} max={60} value={f.limit} onChange={(e) => setF({ ...f, limit: e.target.value })} data-testid="finder-limit-input" />
      <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finder-search-button">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find
      </Button>
    </form>
  );
}

function ProspectRow({ p, checked, onCheck, onStatus, onDelete }) {
  return (
    <div className="flex items-start gap-3 p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`prospect-row-${p.id}`}>
      <Checkbox checked={checked} disabled={p.status === "converted" || p.status === "rejected"} onCheckedChange={onCheck} className="mt-1" data-testid={`prospect-check-${p.id}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{p.name}</span>
          <Badge variant="outline" className="text-[10px] uppercase">{p.source}</Badge>
          {p.is_demo && <Badge className="bg-amber-100 text-amber-800 text-[10px]">DEMO DATA</Badge>}
          <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-testid={`prospect-status-${p.id}`}>{p.status}</Badge>
          {p.in_contacts && p.status !== "converted" && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Already a contact</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          {p.category && <span>{p.category}</span>}
          {p.phone ? <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span> : <span className="text-rose-600">No phone</span>}
          {p.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.address}</span>}
          {p.rating && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />{p.rating} ({p.reviews || 0})</span>}
          {p.followers ? <span>{p.followers.toLocaleString()} subscribers</span> : null}
          {(p.website || p.url) && <a href={p.website || p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline"><Globe className="w-3 h-3" />link</a>}
        </div>
      </div>
      {p.status !== "converted" && (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" title="Save" onClick={() => onStatus("saved")} data-testid={`prospect-save-${p.id}`}><Bookmark className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" title="Reject" onClick={() => onStatus("rejected")} data-testid={`prospect-reject-${p.id}`}><Ban className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" title="Delete" onClick={onDelete} data-testid={`prospect-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}

function UnavailableSources({ items }) {
  return (
    <Card className="p-4 border-slate-200" data-testid="unavailable-sources">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2"><ShieldAlert className="w-4 h-4 text-amber-600" /> Not available (no official search API)</div>
      <div className="space-y-1.5">
        {items.map((u) => <div key={u.id} className="text-xs text-slate-600"><b>{u.label}:</b> {u.reason}</div>)}
      </div>
      <a href="/waggleai-feasibility-report.pdf" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs text-emerald-700 hover:underline" data-testid="feasibility-report-link">Read the full source report (PDF)</a>
    </Card>
  );
}

export default function LeadFinder() {
  const nav = useNavigate();
  const [prov, setProv] = useState({ available: [], unavailable: [] });
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [searchId, setSearchId] = useState(null);
  const [sel, setSel] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  const loadProv = () => api.get("/lead-finder/providers").then((r) => setProv(r.data));
  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (searchId) params.search_id = searchId;
    api.get("/lead-finder/prospects", { params }).then((r) => { setItems(r.data); setSel([]); });
  };
  useEffect(() => { loadProv(); }, []);
  useEffect(load, [status, searchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPStatus = async (id, s) => { try { await api.patch(`/lead-finder/prospects/${id}`, { status: s }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { try { await api.delete(`/lead-finder/prospects/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const convert = async () => {
    try {
      const { data } = await api.post("/lead-finder/prospects/convert", { ids: sel });
      toast.success(`${data.created} new contacts, ${data.linked} matched existing, ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="lead-finder-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Lead Finder</h1>
          <p className="text-sm text-slate-500">Find businesses through official sources, review them, then add the good ones to your CRM.</p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="open-source-settings"><Settings2 className="w-4 h-4 mr-1" /> Source settings</Button>
      </div>

      <Card className="p-4 border-slate-200 space-y-3">
        <SearchBar providers={prov.available} onResults={(id) => { setStatus("all"); setSearchId(id); }} />
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="consent-notice">
          Businesses found here have <b>not</b> agreed to WhatsApp messages. They are added with consent "unknown" and are skipped by campaigns until they opt in.
        </p>
      </Card>

      <div className="grid lg:grid-cols-[1fr_300px] gap-5">
        <Card className="border-slate-200 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-slate-100">
            <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => { setStatus(s); setSearchId(null); }} data-testid={`prospect-filter-${s}`}
                  className={`px-3 py-1 text-xs rounded capitalize ${status === s && !searchId ? "bg-white shadow-sm font-medium" : "text-slate-600"}`}>{s}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {searchId && <Badge className="bg-emerald-50 text-emerald-700" data-testid="latest-search-badge">Latest search</Badge>}
              <Button size="sm" disabled={!sel.length} onClick={convert} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-selected-button">
                <UserPlus className="w-4 h-4 mr-1" /> Add {sel.length || ""} to CRM
              </Button>
              <Button size="sm" variant="ghost" onClick={() => nav("/leads")} data-testid="go-to-leads">Open Leads</Button>
            </div>
          </div>
          <div data-testid="prospect-list">
            {items.length === 0 && <div className="p-10 text-center text-sm text-slate-500" data-testid="prospects-empty">No businesses yet. Run a search above.</div>}
            {items.map((p) => (
              <ProspectRow key={p.id} p={p} checked={sel.includes(p.id)}
                onCheck={(v) => setSel(v ? [...sel, p.id] : sel.filter((x) => x !== p.id))}
                onStatus={(s) => setPStatus(p.id, s)} onDelete={() => del(p.id)} />
            ))}
          </div>
        </Card>
        <UnavailableSources items={prov.unavailable} />
      </div>
      <SourceSettings open={showSettings} onClose={() => setShowSettings(false)} providers={prov.available} onSaved={loadProv} />
    </div>
  );
}
